import React, { useEffect, useState } from 'react';
import { Federation, MailTemplate, Package, PortalDetails, Sponsor, Tag } from "../typings";
import { Align, Row } from "../components/Flex";
import {
    addPortalAdmins, deletePortalSponsor,
    fetchPackages,
    fetchPortal,
    fetchPortalAdmins, fetchTagsForPackage, fetchTagsForPortal,
    updatePortal, updatePortalSponsor
} from "./drills-api";
import { useParams } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../components/buttons/Buttons";
import { toast } from "react-toastify";
import { useApi } from "../api-context";
import { Sketch } from "@uiw/react-color";
import SelectField from "../components/SelectField";
import TextField from "../components/TextField";
import ImageUploading from "react-images-uploading";
import { ImageType } from "react-images-uploading/dist/typings";
import Switch from "react-switch";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { fetchFederations } from "../federation/federations-api";
import { fetchMailTemplates } from "../emails/email-templates-api";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { TailSpin } from "react-loader-spinner";
import ConfirmationDialog from "../tags/ConfirmationDialog";

const TYPES = [
    { value: 'CLUB', text: 'CAN OWN LICENSES' },
    { value: 'ALLIANCE', text: 'NO LICENSES' },
    { value: 'ADMIN', text: 'Drill store (DON\'T USE)' }
];

const sponsorLocations = [
    { id: '1', name: 'Ice dot-zone (2D&3D) (left and right)' },
    { id: '2_1', name: 'Boards, blueline-left (opposite boards as well)' },
    { id: '2_2', name: 'Boards, blueline-right (opposite boards as well)' },
    { id: '3', name: 'Big net (both nets)' },
    { id: '4_1', name: 'Boards rink end, left side of goal (both rink ends)' },
    { id: '4_2', name: 'Boards rink end, right side of goal (both rink ends)' },
    { id: '5_1', name: 'Boards, up-left far side (both boards)' },
    { id: '5_2', name: 'Boards, up-right far side (both boards)' }
];

function PortalPage() {
    const api = useApi();
    const [portal, setPortal] = useState<PortalDetails>();
    const [federations, setFederations] = useState<Federation[]>([]);
    const [mailTemplates, setMailTemplates] = useState<MailTemplate[]>([]);
    const [packages, setPackages] = useState<Package[]>([]);
    const [portalTags, setPortalTags] = useState<Tag[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [admins, setAdmins] = useState<string[]>([]);
    const [newAdmin, setNewAdmin] = useState<string>('');
    const [seed, setSeed] = useState<number>(Math.random());
    const [selectedPackage, setSelectedPackage] = useState<number>();
    const [tagMapping, setTagMapping] = useState<{ [key: number]: number; }>({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { id } = useParams();

    const loadFederations = () => {
        fetchFederations(api, { page: 0, orderBy: 'id', orderDirection: 'ASC', size: 100 })
            .then(res => setFederations(res.items))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load federations');
                }
            });
    };

    const loadMailTemplates = () => {
        fetchMailTemplates(api, { page: 0, orderBy: 'id', orderDirection: 'ASC', size: 100 })
            .then(res => setMailTemplates(res.items))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load mail templates');
                }
            });
    };

    const loadPortalAdmins = () => {
        fetchPortalAdmins(api, id)
            .then(res => setAdmins(res))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load portal admins');
                }
            });
    };

    const loadPackages = () => {
        fetchPackages(api)
            .then(res => setPackages(res))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load packages');
                }
            });
    }

    const loadTags = (id: number) => {
        fetchTagsForPackage(api, id)
            .then(res => setTags(res))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load tags for package');
                }
            });
    }

    const loadPortalTags = () => {
        fetchTagsForPortal(api, Number(id))
            .then(res => setPortalTags(res))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load tags for portal');
                }
            });
    }

    const onUpdatePortal = () => {
        updatePortal(api, Number(id), portal!)
            .then(() => toast.success('Successfully updated file'))
            .catch(() => toast.error('Failed to update file'))
    }

    const loadPortal = () => {
        if (!id) {
            return;
        }
        fetchPortal(api, Number(id))
            .then(setPortal)
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load portal');
                }
            });
    };

    useEffect(() => {
        loadFederations();
        loadMailTemplates();
        loadPortalAdmins();
        loadPortal();
        loadPackages();
        loadPortalTags();
    }, []);

    if (!portal) {
        return null;
    }

    const onUploadPreview = (imageList: ImageType[]) => {
        const formData = new FormData();
        const image = imageList[0];
        formData.append("file", image.file!);

        fetch(`https://api.hockeycoachvision.com/files/organisations/${id}`, {
            method: 'POST',
            body: formData
        })
            .then(() => setSeed(Math.random()))
            .catch(() => toast.error('Failed to upload preview'));
    };

    const onUploadSponsor = (imageList: ImageType[], index: string) => {
        const formData = new FormData();
        const image = imageList[0];
        formData.append("file", image.file!);

        fetch(`https://api.hockeycoachvision.com/files/organisations_${id}/sponsor_${index}`, {
            method: 'POST',
            body: formData
        })
            .then(() => {
                setSeed(Math.random());
                updatePortalSponsor(api, Number(id), index, `sponsor_${index}`)
                    .then(() => {
                        const sponsorImages = portal.sponsorImages || [];
                        if (!sponsorImages.find(i => i.location === index)) {
                            setPortal({ ...portal,
                                sponsorImages: [...(portal.sponsorImages || []), {
                                    name: `sponsor_${index}`,
                                    location: index
                                } as Sponsor]
                            });
                        }
                    });
            })
            .catch(() => toast.error('Failed to upload sponsor image'));
    };

    const onDeleteSponsor = (index: string) => {
        deletePortalSponsor(api, Number(id), index)
            .then(() => {
                setPortal({ ...portal,
                    sponsorImages: portal.sponsorImages.filter(im => im.location !== index)
                });
            });
    };

    const renderSponsorBlock = (index: string) => {
        const description = sponsorLocations.find(sl => sl.id === index)?.name;
        const sponsorImage = portal.sponsorImages.find(s => s.location === index);
        return (
            <ImageUploading
                value={[]}
                onChange={(imageList) => onUploadSponsor(imageList, index)}
                maxNumber={100}
                dataURLKey="data_url"
            >
                {({
                      onImageUpload,
                      dragProps,
                  }) => (
                    <div style={{ flex: 1 }}>
                        <div>{description}</div>
                        {
                            !!sponsorImage && (
                                <img
                                    style={{
                                        maxWidth: '100%'
                                    }}
                                    src={`https://hcv-admin-api.azurewebsites.net/files/fetch/organisations_${id}/${sponsorImage.name}?${seed}`}
                                    alt={'Empty slot'}
                                />
                            )
                        }
                        <button
                            style={{ flex: 0, minWidth: '100px' }}
                            className="my-button"
                            onClick={onImageUpload}
                            {...dragProps}
                        >
                            {`Upload slot ${index}`}
                        </button>
                        <button
                            style={{ flex: 0, minWidth: '100px' }}
                            className="my-button"
                            onClick={() => onDeleteSponsor(index)}
                        >
                            {`Delete slot ${index}`}
                        </button>
                    </div>
                )}
            </ImageUploading>
        );
    };

    const renderSponsorsBlock = () => {
        return (
            <div
                className="box basic-info"
                style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}
            >
                <div className="info-title">Sponsors</div>
                {renderSponsorBlock('1')}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    {renderSponsorBlock('2_1')}
                    {renderSponsorBlock('2_2')}
                </div>
                {renderSponsorBlock('3')}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    {renderSponsorBlock('4_1')}
                    {renderSponsorBlock('4_2')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    {renderSponsorBlock('5_1')}
                    {renderSponsorBlock('5_2')}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="page-container">
                <div className="content-container" style={{ marginBottom: '32px' }}>
                    <div className="box basic-info" style={{ gap: '12px', flex: 3 }}>
                        <TextField
                            label="Name"
                            placeholder="Enter value"
                            value={portal.name}
                            setValue={name => setPortal({ ...portal, name })}
                        />
                        <SelectField
                            label="Portal type"
                            placeholder="Select type"
                            value={portal.type}
                            options={TYPES}
                            setValue={type => setPortal({ ...portal, type })}
                        />
                        <Row gap={12}>
                            <div style={{flex: 1}}>
                                <div className="label">Background color</div>
                                <Sketch
                                    style={{ marginTop: 16 }}
                                    color={portal.backgroundColor}
                                    onChange={(color) => {
                                        setPortal({ ...portal, backgroundColor: color.hex });
                                    }}
                                />
                            </div>
                            <div style={{flex: 1}}>
                                <div className="label">Package color</div>
                                <Sketch
                                    style={{ marginTop: 16 }}
                                    color={portal.packageColor}
                                    onChange={(color) => {
                                        setPortal({ ...portal, packageColor: color.hex });
                                    }}
                                />
                            </div>
                        </Row>
                        <div className="text-field">
                            <div className="label">Expiry</div>
                            <Row gap={8}>
                                <Switch
                                    onChange={ready => setPortal({ ...portal, expiresAt: ready ? new Date().toISOString() : undefined })}
                                    checked={!!portal?.expiresAt} />
                                <DatePicker
                                    disabled={!portal.expiresAt!}
                                    selected={portal.expiresAt ? new Date(portal.expiresAt!) : undefined}
                                    onChange={date => {
                                        setPortal({ ...portal, expiresAt: date?.toISOString() });
                                    }}
                                />
                            </Row>
                        </div>
                        <SelectField
                            label="In organisation"
                            placeholder="Select organisation"
                            value={portal.federationId}
                            options={federations.map(f => ({ value: f.id, text: f.name }))}
                            setValue={federationId => setPortal({ ...portal, federationId })}
                        />
                        <SelectField
                            label="Invite email"
                            placeholder="Select email template"
                            value={portal.inviteTemplateId}
                            options={mailTemplates.map(mt => ({ value: mt.id, text: mt.name }))}
                            setValue={inviteTemplateId => setPortal({ ...portal, inviteTemplateId })}
                        />
                        <SelectField
                            label="Invite email (owned users)"
                            placeholder="Select email template"
                            value={portal.ownedInviteTemplateId}
                            options={mailTemplates.map(mt => ({ value: mt.id, text: mt.name }))}
                            setValue={ownedInviteTemplateId => setPortal({ ...portal, ownedInviteTemplateId })}
                        />
                        <TextField
                            label="Owned user limit"
                            placeholder="Enter value"
                            value={portal.userLimit ? String(portal.userLimit) : ''}
                            setValue={val => setPortal({ ...portal, userLimit: Number(val) })}
                            type="number"
                        />
                        <TextField
                            label="Maximum users limit"
                            placeholder="Enter value"
                            value={portal.maxUserLimit ? String(portal.maxUserLimit) : ''}
                            setValue={val => setPortal({ ...portal, maxUserLimit: Number(val) })}
                            type="number"
                        />
                        <div className="text-field">
                            <div className="label">Can add player users</div>
                            <Switch onChange={ready => setPortal({ ...portal, disabledAddPlayer: !ready })} checked={!portal.disabledAddPlayer} />
                        </div>
                        <PrimaryButton
                            label="SAVE"
                            style={{ flex: 0, minWidth: '150px' }}
                            onClick={onUpdatePortal}
                        />
                    </div>
                    <div style={{ flex: 2 }}>
                        <div
                            className="box basic-info"
                            style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '12px'
                                }}
                            >
                                <div className="info-title">Portal icon</div>
                                <ImageUploading
                                    value={[]}
                                    onChange={onUploadPreview}
                                    maxNumber={100}
                                    dataURLKey="data_url"
                                >
                                    {({
                                          onImageUpload,
                                          dragProps,
                                      }) => (
                                        <button
                                            style={{ flex: 0, minWidth: '100px' }}
                                            className="my-button"
                                            onClick={onImageUpload}
                                            {...dragProps}
                                        >
                                            Upload
                                        </button>
                                    )}
                                </ImageUploading>
                            </div>
                            <div style={{ position: 'relative' }}>
                                <img
                                    style={{
                                        maxWidth: '100%'
                                    }}
                                    src={`https://hcv-admin-api.azurewebsites.net/files/fetch/organisations/${id}.jpg?${seed}`}
                                />
                            </div>
                        </div>
                        <div
                            className="box basic-info"
                            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            <div className="info-title">Admins</div>
                            {admins.map(admin => (
                                <div>{admin}</div>
                            ))}
                            <TextField value={newAdmin} setValue={setNewAdmin}/>
                            <PrimaryButton
                                disabled={!newAdmin}
                                label="Add admin" onClick={() => {
                                addPortalAdmins(api, id || '', newAdmin)
                                    .then(() => {
                                        loadPortalAdmins();
                                        setNewAdmin('');
                                    });
                            }}
                            />
                        </div>
                        <div
                            className="box basic-info"
                            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            <div className="info-title">Clone</div>
                            <SelectField
                                label="Package"
                                placeholder="Select package"
                                value={selectedPackage}
                                options={packages.map(p => ({ value: p.id, text: p.name }))}
                                setValue={id => {
                                    setSelectedPackage(id);
                                    loadTags(id);
                                    setTagMapping({});
                                }}
                            />
                            <PrimaryButton
                                disabled={!selectedPackage}
                                label="Clone"
                                onClick={() => {
                                    setTagMapping(tags.reduce((res: { [key: number]: number; }, val) => {
                                        const matchingTag = portalTags.find(t => t.name === val.name)
                                        res[val.id] = matchingTag ? matchingTag.id : -1;
                                        return res;
                                    }, {}));
                                    setDialogOpen(true);
                                    setLoading(true);
                                }}
                            />
                        </div>
                        <div
                            className="box basic-info"
                            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            <PrimaryButton
                                label="Send invitation to all"
                                onClick={() => setInviteDialogOpen(true)}
                            />
                        </div>
                    </div>
                </div>
                {renderSponsorsBlock()}
            </div>
            <Dialog open={dialogOpen} as="div" onClose={() => {
                setDialogOpen(false);
            }} className="dialog-container">
                <DialogBackdrop className="dialog-backdrop"/>
                <div className="dialog">
                    <DialogPanel className="dialog-box">
                        <DialogTitle as="div" className="dialog-title">
                            Clone package
                        </DialogTitle>
                        <div>Map existing tags</div>
                        <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
                            {tags.map(tag => {
                                return (
                                    <Row align={Align.SPACE_BETWEEN} gap={4}>
                                        <div>{tag.name}</div>
                                        <SelectField
                                            placeholder="Select tag"
                                            value={tagMapping[tag.id]}
                                            options={[
                                                ...portalTags.map(p => ({ value: p.id, text: p.name })),
                                                { value: -1, text: 'Clone this tag' },
                                                { value: -2, text: 'Ignore this tag' }
                                            ]}
                                            setValue={id => {
                                                setTagMapping({
                                                    ...tagMapping,
                                                    [tag.id]: id
                                                })
                                            }}
                                        />
                                    </Row>
                                );
                            })}
                        </div>
                        <Row gap={8} style={{ justifyContent: 'flex-end', marginTop: '8px' }}>
                            <PrimaryButton label="Clone" onClick={() => {
                                setLoading(true);
                                fetch(`https://api.hockeycoachvision.com/newweb/organisation/${id}/clone-article/${selectedPackage}`, {
                                    method: 'POST',
                                    body: JSON.stringify(tagMapping),
                                    headers: {
                                        "Content-Type": "application/json"
                                    }
                                }).then(() => {
                                    setDialogOpen(false);
                                    toast.info('Cloned package')
                                }).catch(() => toast.error('Failed to clone package'))
                                    .finally(() => setLoading(false));
                            }} />
                            <SecondaryButton label="Cancel" onClick={() => {
                                setDialogOpen(false);
                            }} />
                        </Row>
                    </DialogPanel>
                </div>
            </Dialog>
            <ConfirmationDialog
                title="Send invitation"
                description="Are you sure you want to send the invitation to all members?"
                open={inviteDialogOpen}
                onClose={() => setInviteDialogOpen(false)}
                onConfirm={() => {
                    setLoading(true);
                    setInviteDialogOpen(false);
                    fetch(`https://api.hockeycoachvision.com/newweb/organisation/${id}/invite-all`, {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json"
                        }
                    })
                        .then(() => {
                            toast.info('Sent invitations')
                        })
                        .catch(() => toast.error('Error sending invitations'))
                        .finally(() => {
                            setLoading(false);
                        });
                }}
            />
        </>
    );
}

export default PortalPage;
